import type { AuditEvent, CustomerActivityEvent } from '@shared/contracts/entities';
import { nowIso } from './clock';

/**
 * Audit and customer-activity foundation.
 *
 * Package rules:
 *  - "Canonical status, activity, notification and immutable audit evidence."
 *  - AuditEvent carries `immutable: true` in the shared contract, so the store
 *    is append-only and hands out frozen copies.
 *  - Customer-visible activity is a separate projection: internal risk notes
 *    must never reach the customer surface.
 */

let sequence = 0;

function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}_${String(sequence).padStart(6, '0')}`;
}

const events: AuditEvent[] = [];

export type RecordAuditInput = {
  readonly companyId: string;
  readonly programmeId: string;
  readonly actorName: string;
  readonly actorType: AuditEvent['actorType'];
  readonly action: string;
  readonly objectType: string;
  readonly objectId: string;
  readonly customerVisible: boolean;
  /** Plain-language line shown to the customer when `customerVisible`. */
  readonly summary?: string;
  readonly before?: Record<string, unknown>;
  readonly after?: Record<string, unknown>;
  readonly reason?: string;
  readonly permissionUsed?: string;
  readonly metadata?: Record<string, unknown>;
};

export function recordAudit(input: RecordAuditInput): AuditEvent {
  const event: AuditEvent = Object.freeze({
    id: nextId('evt'),
    companyId: input.companyId,
    programmeId: input.programmeId,
    actorName: input.actorName,
    actorType: input.actorType,
    action: input.action,
    objectType: input.objectType,
    objectId: input.objectId,
    createdAt: nowIso(),
    customerVisible: input.customerVisible,
    immutable: true,
    ...(input.before !== undefined ? { before: input.before } : {}),
    ...(input.after !== undefined ? { after: input.after } : {}),
    ...(input.reason !== undefined ? { reason: input.reason } : {}),
    ...(input.permissionUsed !== undefined ? { permissionUsed: input.permissionUsed } : {}),
    ...(input.metadata !== undefined
      ? { metadata: { ...input.metadata, ...(input.summary ? { summary: input.summary } : {}) } }
      : input.summary
        ? { metadata: { summary: input.summary } }
        : {}),
  });
  events.push(event);
  return event;
}

/**
 * Human-readable line for an audit event.
 *
 * `metadata` is typed `Record<string, unknown>` on the shared contract, so the
 * summary is narrowed here rather than cast at every call site.
 */
export function auditSummary(event: AuditEvent): string {
  const summary = event.metadata?.['summary'];
  return typeof summary === 'string' ? summary : event.action;
}

export function auditEvents(companyId: string): readonly AuditEvent[] {
  return events.filter((e) => e.companyId === companyId);
}

export function auditEventsFor(
  companyId: string,
  objectType: string,
  objectId: string,
): readonly AuditEvent[] {
  return events.filter(
    (e) => e.companyId === companyId && e.objectType === objectType && e.objectId === objectId,
  );
}

/**
 * Customer-facing projection. Drops anything not customer-visible and strips
 * the internal before/after and permission fields.
 */
export function customerActivity(companyId: string): readonly CustomerActivityEvent[] {
  return events
    .filter((e) => e.companyId === companyId && e.customerVisible)
    .map((e) => {
      const summary = auditSummary(e);
      return Object.freeze({
        id: e.id,
        companyId: e.companyId,
        actorName: e.actorName,
        actorType: e.actorType,
        action: e.action,
        objectType: e.objectType,
        objectId: e.objectId,
        createdAt: e.createdAt,
        customerVisible: true as const,
        summary,
      });
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function resetAudit(): void {
  events.length = 0;
  sequence = 0;
}

/** Seed pre-existing history without pretending it happened just now. */
export function seedAudit(seeded: readonly AuditEvent[]): void {
  events.push(...seeded.map((e) => Object.freeze({ ...e })));
}
